import { useRef, useEffect, useState, useCallback } from 'react'
import { Box, Stack, Text, Group, ScrollArea, Loader, ActionIcon, Tooltip, Button } from '@mantine/core'
import { IconChevronsUp, IconChevronsDown, IconArrowRight, IconArrowLeft, IconSend, IconCheck } from '@tabler/icons-react'
import { ScrollDownIndicator } from './ScrollDownIndicator'
import { ChoiceButtons } from './ChoiceButtons'
import { InputPanel } from './InputPanel'
import { BeatTimeline } from './BeatTimeline'
import { GeneratedContentPanel } from './GeneratedContentPanel'
import { MarkdownRenderer } from '../common/MarkdownRenderer'
import { useTypewriter } from '../../hooks/useTypewriter'
import { getBeatTypeColor } from '../../lib/beatColors'

export function DialoguePanel({
  currentBeat,
  character,
  characters,
  selectedChoice,
  inputValue,
  generatedContent,
  isLoading,
  canGoNext,
  onNext,
  onChoiceSelect,
  onInputSubmit,
  onInputChange,
  expanded,
  onToggleExpand,
  canGoPrev,
  onPrev,
  locked = false,
  timeline,
}) {
  const beatContent = currentBeat?.content || ''
  const isNarrative = currentBeat?.beat_type === 'narrative'
  const isDecision = currentBeat?.beat_type === 'decision'
  const isInput = currentBeat?.beat_type === 'input'
  const isCharacterResponse = currentBeat?.beat_type === 'character_response'
  const isNarrativeResponse = currentBeat?.beat_type === 'narrative_response'
  const isResolution = currentBeat?.beat_type === 'resolution'

  const { displayed, isDone, skip } = useTypewriter(beatContent, {
    enabled: isNarrative,
    beatId: currentBeat?.id,
  })

  const speakerName = character?.name || currentBeat?.name || 'Narrator'
  const speakerRole = character?.role || ''
  const beatColor = getBeatTypeColor(currentBeat?.beat_type)

  const viewportRef = useRef(null)

  // Scroll indicator state
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)

  const checkOverflow = useCallback(() => {
    const el = viewportRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20
    setShowScrollIndicator(el.scrollHeight > el.clientHeight && !nearBottom)
  }, [])

  // Make viewport's inner div stretch so flex children (like textarea) can fill height
  useEffect(() => {
    if (viewportRef.current) {
      const inner = viewportRef.current.firstElementChild
      if (inner) {
        inner.style.minHeight = '100%'
        inner.style.display = 'flex'
        inner.style.flexDirection = 'column'
      }
    }
  }, [currentBeat?.id])

  // Scroll to top when collapsing back (after grid transition completes)
  useEffect(() => {
    if (!expanded && viewportRef.current) {
      const timer = setTimeout(() => {
        if (viewportRef.current) {
          viewportRef.current.scrollTop = 0
          checkOverflow()
        }
      }, 350)
      return () => clearTimeout(timer)
    }
  }, [expanded, checkOverflow])

  // Reset scroll to top on beat change
  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = 0
    }
    setShowScrollIndicator(false)
  }, [currentBeat?.id])

  // ResizeObserver to detect content height changes (typewriter growth, AI streaming)
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const observer = new ResizeObserver(() => checkOverflow())
    observer.observe(el)
    // Also observe the inner content container
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => observer.disconnect()
  }, [currentBeat?.id, checkOverflow])

  // Click content area to skip typewriter
  const handleContentClick = () => {
    if (!isDone) {
      skip()
    }
  }

  if (isLoading) {
    return (
      <Box
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(to top, rgba(6, 14, 26, 0.96), rgba(10, 22, 40, 0.92))',
          borderTop: '1px solid rgba(0, 255, 255, 0.1)',
        }}
      >
        <Loader size="sm" color="cyan" />
      </Box>
    )
  }

  if (!currentBeat) {
    return (
      <Box
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(to top, rgba(6, 14, 26, 0.96), rgba(10, 22, 40, 0.92))',
          borderTop: '1px solid rgba(0, 255, 255, 0.1)',
        }}
      >
        <Text c="dimmed">No content available</Text>
      </Box>
    )
  }

  // Render the action button based on beat type
  const renderActionContent = () => {
    // Locked beats (already-answered decisions/inputs being revisited) always
    // advance — the answer is fixed, so skip the choice/submit prompts and fall
    // through to the Next/Complete button below.
    if (!locked) {
      if (isDecision && !selectedChoice) {
        return (
          <Text size="sm" c="dimmed" fs="italic" ff="Rajdhani, sans-serif">
            Select a choice to continue
          </Text>
        )
      }

      if (isInput) {
        return (
          <Button
            size="sm"
            color={beatColor}
            variant="light"
            rightSection={<IconSend size={16} />}
            onClick={onInputSubmit}
            disabled={!inputValue?.trim()}
            style={{
              border: `1px solid var(--mantine-color-${beatColor}-9)`,
            }}
          >
            Submit
          </Button>
        )
      }
    }

    const isLastBeat = !canGoNext

    return (
      <Button
        size="sm"
        color={beatColor}
        variant="light"
        rightSection={isLastBeat ? <IconCheck size={16} /> : <IconArrowRight size={16} />}
        onClick={onNext}
        disabled={isNarrative && !isDone}
        style={{
          border: `1px solid var(--mantine-color-${beatColor}-9)`,
        }}
      >
        {isLastBeat ? 'Complete' : 'Next'}
      </Button>
    )
  }

  return (
    <Box
      className="crt-terminal"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(to top, rgba(6, 14, 26, 0.96), rgba(10, 22, 40, 0.92))',
        borderTop: '1px solid rgba(0, 255, 255, 0.1)',
      }}
    >
      {/* Speaker header with expand toggle */}
      <Box px="lg" pt="md" pb="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <Text
              size="md"
              fw={700}
              c="cyan.3"
              ff="var(--bw-heading-font)"
              style={{ letterSpacing: 'var(--bw-heading-letter-spacing, 0.05em)' }}
            >
              {speakerName}
            </Text>
            {speakerRole && (
              <Text size="xs" c="dimmed">
                — {speakerRole}
              </Text>
            )}
          </Group>
          <Group gap={6}>
            <Tooltip label={expanded ? 'Collapse panel' : 'Expand panel'} position="bottom">
              <ActionIcon
                variant="light"
                color="cyan"
                size="md"
                onClick={onToggleExpand}
                style={{
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  boxShadow: '0 0 8px rgba(0, 255, 255, 0.15)',
                }}
              >
                {expanded ? <IconChevronsDown size={18} /> : <IconChevronsUp size={18} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>

      {/* Content area — click to skip typewriter */}
      <Box style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <ScrollArea
          viewportRef={viewportRef}
          style={{ height: '100%', cursor: !isDone ? 'pointer' : 'default' }}
          px="lg"
          pt={5}
          pb="lg"
          onClick={handleContentClick}
          onScrollPositionChange={checkOverflow}
        >
          <Stack gap="sm" style={{ flex: 1 }}>
            {/* Beat text content — size inherited from .markdown-content
                (theme content-base * S/M/L scale) */}
            <Box style={{ lineHeight: 1.5 }}>
              <MarkdownRenderer content={isNarrative ? displayed : beatContent} />
            </Box>

            {/* Generated content for AI beats */}
            {(isCharacterResponse || isNarrativeResponse || isResolution) && (
              <GeneratedContentPanel content={generatedContent} beatType={currentBeat.beat_type} characters={characters} />
            )}

            {/* Decision choices */}
            {isDecision && currentBeat.options?.choices?.length > 0 && (
              <Box onClick={e => e.stopPropagation()}>
                <ChoiceButtons
                  choices={currentBeat.options.choices.sort((a, b) => a.position - b.position)}
                  selectedChoice={selectedChoice}
                  onSelect={onChoiceSelect}
                  locked={locked}
                />
              </Box>
            )}

            {/* Input area */}
            {isInput && (
              <Box onClick={e => e.stopPropagation()} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <InputPanel
                  value={inputValue}
                  onChange={onInputChange}
                  onSubmit={onInputSubmit}
                  locked={locked}
                />
              </Box>
            )}
          </Stack>
        </ScrollArea>
        <ScrollDownIndicator viewportRef={viewportRef} visible={showScrollIndicator} />
      </Box>

      {/* Review timeline — jump back to any seen beat/thread, or skip to current */}
      {timeline && (
        <BeatTimeline
          threads={timeline.threads}
          beats={timeline.beats}
          currentThreadIndex={timeline.currentThreadIndex}
          currentIndex={timeline.currentIndex}
          liveThreadIndex={timeline.liveThreadIndex}
          liveBeatIndex={timeline.liveBeatIndex}
          isReachable={timeline.isReachable}
          onJump={timeline.onJump}
          onSkipToCurrent={timeline.onSkipToCurrent}
        />
      )}

      {/* Action bar — 3-column grid keeps center content perfectly centered */}
      <Box
        px="lg"
        py="sm"
        style={{
          borderTop: `2px solid var(--mantine-color-${beatColor}-9)`,
          display: 'grid',
          gridTemplateColumns: '40px 1fr 40px',
          alignItems: 'center',
          height: 52,
          flexShrink: 0,
        }}
      >
        {/* Left: back button */}
        <Box style={{ justifySelf: 'start' }}>
          {canGoPrev && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="md"
              onClick={onPrev}
              style={{ opacity: 0.6, transition: 'opacity 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1' }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6' }}
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
          )}
        </Box>
        {/* Center: main action */}
        <Box style={{ justifySelf: 'center' }}>
          {renderActionContent()}
        </Box>
        {/* Right: empty spacer for symmetry (text-size control lives in the
            persistent story bar at the top of GameScreen) */}
        <Box />
      </Box>
    </Box>
  )
}
